import { Box, TableContainer } from '@chakra-ui/react'
import paginate from 'paginate-array'
import {
  createContext,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import sortObjectsArray from 'sort-objects-array'
import { useImmer } from 'use-immer'

import type {
  ITableGridProps,
  ITableGridContext,
  ITableGridOrder
} from '@base/@types/components/Layout/TableGrid'

import TableGridBody from './Body'
import TableGridBodyFollowUp from './BodyPersonalized'
import TableGridBodyPurchaseProcess from './BodyPersonalized/indexProcess'
import TableGridBodyFollowUpReab from './BodyPersonalized/indexReab'
import TableGridFooter from './Footer'
import TableGridHeader from './Header'
import TableGridPagination from './Pagination'
import TableGridPaginationBack from './PaginationBack'

const TableGrid: FC<ITableGridProps> = ({
  id,
  headers,
  data,
  query = '',
  actions,
  isActions = true,
  height,
  loading,
  isPaged = false,
  pagination,
  onSelect,
  entity,
  undoValue,
  dataFooter,
  isActionFooter = false,
  isFooter = false,
  isPagedBack = false,
  itsSortable = false,
  itsSortableGroup = false,
  isModal,
  selected,
  updateIsGraphic,
  udatePageChange,
  updatedFinalStorage,
  updateIsModalConfig,
  context,
  isOverflowDisabled = false
}) => {
  const [page, setPage] = useState(1)

  const [order, updateOrder] = useImmer<ITableGridOrder>({
    column: '',
    sort: null
  })

  const columns = useMemo(() => {
    const arr = []

    for (const row of headers) {
      const columns = row?.columns
      const available = []

      if (columns) {
        for (const column of columns) {
          if (column?.show === true || column?.isAlwaysVisible === true) {
            available.push(column)
          }
        }
      }

      if (!row.id && available.length === 0) {
        continue
      }

      arr.push({
        ...row,
        columns: available
      })
    }

    return arr
  }, [headers])

  const withSearch = useCallback(
    (row: any) =>
      JSON.stringify(row).toLowerCase().includes(query!.toLowerCase()),
    [query]
  )
  const isDateString = (value: any): boolean => {
    if (typeof value !== 'string') return false
    const trimmed = value.trim()
    // DD/MM/YYYY (formato brasileiro)
    const slashParts = trimmed.split(' ')[0].split('/')
    if (slashParts.length === 3) {
      const [d, m, y] = slashParts
      if (
        y.length === 4 &&
        !isNaN(Number(d)) &&
        !isNaN(Number(m)) &&
        !isNaN(Number(y)) &&
        Number(y) > 1900
      ) {
        return true
      }
    }
    // YYYY-MM-DD (ISO)
    const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (isoMatch && Number(isoMatch[1]) > 1900) return true
    return false
  }

  const parseDateValue = (value: string): number => {
    const trimmed = value.trim()
    // DD/MM/YYYY
    const slashParts = trimmed.split(' ')[0].split('/')
    if (slashParts.length === 3 && slashParts[2].length === 4) {
      return new Date(
        Number(slashParts[2]),
        Number(slashParts[1]) - 1,
        Number(slashParts[0])
      ).getTime()
    }
    // YYYY-MM-DD
    const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (isoMatch) {
      return new Date(
        Number(isoMatch[1]),
        Number(isoMatch[2]) - 1,
        Number(isoMatch[3])
      ).getTime()
    }
    return 0
  }

  const compareValues = (key: any, order = 'asc') => {
    return function innerSort(
      a: { [x: string]: any; hasOwnProperty: (arg0: any) => any },
      b: { [x: string]: any; hasOwnProperty: (arg0: any) => any }
    ) {
      // eslint-disable-next-line no-prototype-builtins
      if (!a.hasOwnProperty(key) || !b.hasOwnProperty(key)) {
        return 0
      }
      try {
        const varA = typeof a[key] === 'string' ? a[key].toUpperCase() : a[key]
        const varB = typeof b[key] === 'string' ? b[key].toUpperCase() : b[key]
        if (isDateString(a[key]) && isDateString(b[key])) {
          const dateA = parseDateValue(String(a[key]))
          const dateB = parseDateValue(String(b[key]))
          let comparison = 0
          if (dateA > dateB) comparison = 1
          else if (dateA < dateB) comparison = -1
          return order === 'desc' ? comparison * -1 : comparison
        }
        if (typeof varA === 'number' && typeof varB === 'number') {
          let comparison = 0
          if (varA > varB) {
            comparison = 1
          } else if (varA < varB) {
            comparison = -1
          }
          return order === 'desc' ? comparison * -1 : comparison
        }
        let varAN = 0
        let varBN = 0
        let condition = false
        let conditionA = false
        let conditionB = false
        if (key === 'compra-desejada') {
          const varANCD = varA.substring(1, 100)
          const varBNCD = varB.substring(1, 100)

          let varAT =
            typeof varANCD === 'string'
              ? varANCD.includes('.')
                ? varANCD.replaceAll('.', '')
                : varANCD
              : varANCD
          varAT =
            typeof varAT === 'string'
              ? varAT.includes(',')
                ? varAT.replaceAll(',', '.')
                : varAT
              : varAT

          let varBT =
            typeof varBNCD === 'string'
              ? varBNCD.includes('.')
                ? varBNCD.replaceAll('.', '')
                : varBNCD
              : varBNCD
          varBT =
            typeof varBT === 'string'
              ? varBT.includes(',')
                ? varBT.replaceAll(',', '.')
                : varBT
              : varBT
          varBT = Number(varBT)
          varAT = Number(varAT)
          let comparison = 0
          if (varAT > varBT) {
            comparison = 1
          } else if (varAT < varBT) {
            comparison = -1
          }
          return order === 'desc' ? comparison * -1 : comparison
        }
        if (
          typeof varA === 'string' &&
          (varA?.includes(',') || varA?.includes('.')) &&
          !varA.includes(' ')
          // !isNaN(parseFloat(varB)) ||
          // varB?.includes(',') ||
          // varB?.includes('.')
        ) {
          condition = true
          conditionA = true
          // console.log('number')
          // const comparison = 0
          // if (varA === '836.271' || varB === '836.271') {
          //   console.log('achou')
          // }
          // if (isNaN(varA)) {
          //   if (varA.includes('.')) {
          //     const teste = varA.replaceAll('.', '')
          //     console.log('teste', teste)
          //   }
          // }
          let varAT =
            typeof varA === 'string'
              ? varA.includes('.')
                ? varA.replaceAll('.', '')
                : varA
              : varA
          // let varBT =
          //   typeof varB === 'string'
          //     ? varB.includes('.')
          //       ? varB.replaceAll('.', '')
          //       : varB
          //     : varB
          varAT =
            typeof varAT === 'string'
              ? varAT.includes(',')
                ? varAT.replaceAll(',', '.')
                : varAT
              : varAT
          // varBT =
          //   typeof varBT === 'string'
          //     ? varBT.includes(',')
          //       ? varBT.replaceAll(',', '.')
          //       : varBT
          //     : varBT
          varAN = parseFloat(parseFloat(varAT).toFixed(2))
          // const varBN = parseFloat(parseFloat(varBT).toFixed(2))
          // if (varAN > varBN) {
          //   comparison = 1
          // } else if (varAN < varBN) {
          //   comparison = -1
          // }
          // return order === 'desc' ? comparison * -1 : comparison
        } else {
          varAN = varA
        }
        if (
          typeof varB === 'string' &&
          (varB?.includes(',') || varB?.includes('.')) &&
          !varB.includes(' ')
        ) {
          condition = true
          conditionB = true

          //  let varAT =
          //    typeof varA === 'string'
          //      ? varA.includes('.')
          //        ? varA.replaceAll('.', '')
          //        : varA
          //      : varA
          let varBT =
            typeof varB === 'string'
              ? varB.includes('.')
                ? varB.replaceAll('.', '')
                : varB
              : varB
          //  varAT =
          //    typeof varAT === 'string'
          //      ? varAT.includes(',')
          //        ? varAT.replaceAll(',', '.')
          //        : varAT
          //      : varAT
          varBT =
            typeof varBT === 'string'
              ? varBT.includes(',')
                ? varBT.replaceAll(',', '.')
                : varBT
              : varBT
          //  const varAN = parseFloat(parseFloat(varAT).toFixed(2))
          varBN = parseFloat(parseFloat(varBT).toFixed(2))
          // if (varAN > varBN) {
          //   comparison = 1
          // } else if (varAN < varBN) {
          //   comparison = -1
          // }
          // return order === 'desc' ? comparison * -1 : comparison
        } else {
          varBN = varB
        }
        if (varA !== '' && !isNaN(varA) && !conditionA) {
          condition = true
          varAN = Number(varA)
        }
        if (varB !== '' && !isNaN(varB) && !conditionB) {
          condition = true

          varBN = Number(varB)
        }
        if (condition) {
          let comparison = 0

          if (varAN > varBN) {
            comparison = 1
          } else if (varAN < varBN) {
            comparison = -1
          }
          return order === 'desc' ? comparison * -1 : comparison
        }
        let comparison = 0
        if (varA > varB) {
          comparison = 1
        } else if (varA < varB) {
          comparison = -1
        }
        return order === 'desc' ? comparison * -1 : comparison
      } catch (ex) {}
    }
  }

  const rows = useMemo(() => {
    if (query!.length > 0) {
      const toSearch = data.filter(withSearch)

      return paginate(
        order.column !== ''
          ? sortObjectsArray(toSearch, order.column, order.sort)
          : toSearch,
        page,
        isPaged ? 150 : data?.length
      )
    }

    return paginate(
      order.column !== ''
        ? data.sort(compareValues(order.column, order.sort))
        : data,
      page,
      isPaged ? 150 : data?.length
    )
    // compareValues é uma função pura recriada a cada render; incluí-la recalcularia a ordenação sempre
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, page, isPaged, order, withSearch, query])

  const createDiv = (_height: number) => {
    const div = document.createElement('div')

    // div.style.top = '0'
    // div.style.right = '0'
    // div.style.width = '5px'
    // div.style.position = 'absolute'
    // div.style.cursor = 'col-resize'
    // div.style.userSelect = 'none'
    // div.style.height = height + 'px'

    return div
  }

  const getStyleVal = (elm: any, css: any) => {
    return window.getComputedStyle(elm, null).getPropertyValue(css)
  }

  const paddingDiff = useCallback((col: any) => {
    if (getStyleVal(col, 'box-sizing') === 'border-box') {
      return 0
    }

    const padLeft = getStyleVal(col, 'padding-left')
    const padRight = getStyleVal(col, 'padding-right')
    return parseInt(padLeft) + parseInt(padRight)
  }, [])

  const setListeners = useCallback(
    (div: any) => {
      let pageX: any
      let curCol: any
      let curColWidth: any
      let tableWidth: any

      div.addEventListener('mousedown', function (e: any) {
        tableWidth = document.getElementById(`scl-tablegrid-${id}`)!.offsetWidth
        curCol = e.target.parentElement
        pageX = e.pageX

        const padding = paddingDiff(curCol)

        curColWidth = curCol.offsetWidth - padding
      })

      div.addEventListener('mouseover', function (e: any) {
        e.target.style.borderRight = '2px solid var(--chakra-colors-yellow-400)'
      })

      div.addEventListener('mouseout', function (e: any) {
        e.target.style.borderRight = ''
      })

      document.addEventListener('mousemove', function (e) {
        if (curCol) {
          const diffX = e.pageX - pageX

          curCol.style.width = curColWidth + diffX + 'px'
          document.getElementById(`scl-tablegrid-${id}`)!.style.width =
            tableWidth + diffX + 'px'
        }
      })

      document.addEventListener('mouseup', function () {
        curCol = undefined
        pageX = undefined
        curColWidth = undefined
      })
    },
    [id, paddingDiff]
  )

  const resizableGrid = useCallback(
    table => {
      const rows = table.getElementsByTagName('tr')

      table.style.overflow = 'hidden'

      const tableHeight = table.offsetHeight

      for (let n = 0; n < 2; n++) {
        const row = rows[n]
        const cols = row ? row.children : undefined

        for (let i = 0; i < cols.length; i++) {
          const div = createDiv(tableHeight)
          cols[i].appendChild(div)
          cols[i].style.position = 'relative'

          setListeners(div)
        }
      }
    },
    [setListeners]
  )

  const handlePageChange = (page: number) => {
    setPage(page + 1)
  }

  const handleOrder = (id: string) => {
    let sort: 'asc' | 'desc' | null = null

    if (order.column === id) {
      if (order.sort === 'desc') {
        sort = 'asc'
      }
    } else {
      sort = 'desc'
    }

    updateOrder(draft => {
      draft.column = sort === null ? '' : id
      draft.sort = sort
    })
  }

  useEffect(() => {
    const table = document.querySelector(`#scl-tablegrid-${id}`)

    resizableGrid(table)
  }, [id, resizableGrid])

  return (
    <TableGridContext.Provider
      value={{
        columns,
        dataFooter: dataFooter || [],
        data: rows,
        page,
        query: query || '',
        handlePageChange: onSelect || handlePageChange,
        loading: loading || false,
        order,
        handleOrder,
        itsSortable,
        itsSortableGroup,
        actions,
        entity: entity || '',
        pagination: pagination,
        context: context
      }}
    >
      {isOverflowDisabled ? (
        <TableContainer>
          <table id={`scl-tablegrid-${id}`} className="scl-table-grid">
            <TableGridHeader
              isModal={isModal}
              selected={selected}
              isActions={isActions}
              updateIsModalConfig={updateIsModalConfig}
            />

            {entity === 'alert-followUp' ? (
              <TableGridBodyFollowUp />
            ) : entity === 'alert-followUp-reab' ? (
              <TableGridBodyFollowUpReab />
            ) : (
              <TableGridBody
                key={`tableGridBody-${Number(id) + 1}`}
                undoValue={undoValue}
                updateIsGraphic={updateIsGraphic}
                updatedFinalStorage={updatedFinalStorage}
              />
            )}

            {isFooter ? <TableGridFooter isAction={isActionFooter} /> : <></>}
          </table>
        </TableContainer>
      ) : (
        <Box
          h={id === 'sales-table' ? data.length * 41 + 'px' : height}
          maxW={
            id !== 'alert-followUp' && id !== 'alert-followUp-reab'
              ? '100%'
              : ''
          }
          overflow={
            id !== 'alert-followUp' &&
            id !== 'alert-followUp-reab' &&
            id !== 'kpis'
              ? 'auto'
              : ''
          }
          // overflowX={id === 'sales-table' ? 'scroll' : null}
        >
          <table id={`scl-tablegrid-${id}`} className="scl-table-grid">
            <TableGridHeader
              isModal={isModal}
              selected={selected}
              isActions={isActions}
              updateIsModalConfig={updateIsModalConfig}
            />
            {entity === 'alert-followUp' ? (
              <TableGridBodyFollowUp />
            ) : entity === 'alert-followUp-reab' ? (
              <TableGridBodyFollowUpReab />
            ) : entity === 'alert-purchase-proces' ? (
              <TableGridBodyPurchaseProcess />
            ) : (
              <TableGridBody
                key={`tableGridBody-${Number(id) + 1}`}
                undoValue={undoValue}
                updateIsGraphic={updateIsGraphic}
                updatedFinalStorage={updatedFinalStorage}
              />
            )}

            {isFooter ? <TableGridFooter isAction={isActionFooter} /> : <></>}
          </table>
        </Box>
      )}

      {isPaged && <TableGridPagination />}
      {isPagedBack && (
        <TableGridPaginationBack udatePageChange={udatePageChange} />
      )}
    </TableGridContext.Provider>
  )
}

export default TableGrid
export const TableGridContext = createContext<ITableGridContext>(
  {} as ITableGridContext
)
